import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requiredRoles?: string[];
  requireAllRoles?: boolean;
  forbiddenRoles?: string[];
  forbiddenOverrideRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAllRoles = false,
  forbiddenRoles = [],
  forbiddenOverrideRoles = ['ADMIN', 'SUPER_ADMIN', 'CORPORATE_ADMIN'],
}) => {
  const { isAuthenticated, isLoading, user, hasAnyRole, hasAllRoles, getCurrentUser } = useAuth();
  const location = useLocation();

  // Try to load user data if authenticated but user data is missing
  useEffect(() => {
    if (isAuthenticated && !user && !isLoading) {
      getCurrentUser().catch(() => {
        // If fetching user fails, redirect to login
      });
    }
  }, [isAuthenticated, user, isLoading, getCurrentUser]);

  // Show loading spinner while checking authentication
  if (isLoading || (isAuthenticated && !user)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRoles.length > 0) {
    const hasAccess = requireAllRoles
      ? hasAllRoles(requiredRoles)
      : hasAnyRole(requiredRoles);

    if (!hasAccess) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h4" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            You do not have permission to access this page.
            <br />
            Required role{requiredRoles.length > 1 ? 's' : ''}: {requiredRoles.join(', ')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your roles: {user?.roles?.join(', ') || 'None'}
          </Typography>
        </Box>
      );
    }
  }

  if (forbiddenRoles.length > 0) {
    const hasForbiddenRole = hasAnyRole(forbiddenRoles);
    const hasOverrideRole = hasAnyRole(forbiddenOverrideRoles);
    if (hasForbiddenRole && !hasOverrideRole) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h4" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            This page is not available for your role.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your roles: {user?.roles?.join(', ') || 'None'}
          </Typography>
        </Box>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
